import React, { useState, useMemo } from 'react';
import { useParams, useNavigate } from 'react-router-dom';
import { useLanguage } from '@/context/LanguageContext';
import { useMenuData } from '@/context/MenuDataContext';
import { allergenInfo } from '@/data/menuData';
import Header from './Header';
import Footer from './Footer';
import { Badge } from '@/components/ui/badge';
import { Dialog, DialogContent, DialogHeader, DialogTitle } from '@/components/ui/dialog';
import { Flame, Leaf, Heart, Star, AlertTriangle, X, Play } from 'lucide-react';

const AllergenBadge = ({ allergen }) => {
  const { language } = useLanguage();
  const info = allergenInfo[allergen];

  if (!info) return null;

  return (
    <span
      className="inline-flex items-center gap-1 px-2 py-0.5 rounded-full bg-secondary/20 text-secondary-foreground text-xs"
      title={info[language]}
    >
      <span>{info.icon}</span>
      <span className="hidden sm:inline">{info[language]}</span>
    </span>
  );
};

const MenuItemCard = ({ item, onClick, index }) => {
  const { getText, t } = useLanguage();
  const [imageLoaded, setImageLoaded] = useState(false);

  // Optimize image URL
  const getOptimizedSrc = (url) => {
    if (!url) return url;
    if (url.includes('unsplash.com')) {
      const hasParams = url.includes('?');
      return `${url}${hasParams ? '&' : '?'}w=400&q=75&auto=format`;
    }
    if (url.includes('pexels.com')) {
      const hasParams = url.includes('?');
      return `${url}${hasParams ? '&' : '?'}auto=compress&cs=tinysrgb&w=400`;
    }
    return url;
  };

  return (
    <div
      onClick={() => onClick(item)}
      className="bg-card rounded-xl overflow-hidden shadow-elegant hover-lift cursor-pointer border border-border/30 hover:border-primary/30 transition-all animate-fade-in-up"
      style={{ animationDelay: `${index * 0.05}s` }}
    >
      {/* Image */}
      <div className="relative aspect-[16/10] overflow-hidden">
        {/* Skeleton */}
        {!imageLoaded && (
          <div className="absolute inset-0 bg-gradient-to-r from-charcoal via-charcoal-light to-charcoal animate-pulse" />
        )}
        
        <img
          src={getOptimizedSrc(item.image)}
          alt={getText(item.name)}
          className={`w-full h-full transition-all duration-500 hover:scale-105 ${
            imageLoaded ? 'opacity-100' : 'opacity-0'
          } ${item.objectFit === 'contain' ? 'object-contain bg-white/5' : 'object-cover'}`}
          loading="lazy"
          decoding="async"
          onLoad={() => setImageLoaded(true)}
          onError={(e) => {
            if (e.currentTarget.src.indexOf('/images/menu/placeholder.webp') === -1) {
              e.currentTarget.src = '/images/menu/placeholder.webp';
              setImageLoaded(true);
            }
          }}
        />

        {/* Video indicator badge - small icon */}
        {item.hasVideo && (
          <div className="absolute top-2 right-2">
            <div className="w-8 h-8 rounded-full bg-primary/80 flex items-center justify-center shadow-lg">
              <Play className="w-4 h-4 text-primary-foreground ml-0.5" fill="currentColor" />
            </div>
          </div>
        )}

        {/* Badges overlay */}
        <div className="absolute top-2 left-2 flex flex-wrap gap-1">
          {item.isSignature && (
            <Badge className="bg-primary text-primary-foreground text-xs px-2 py-0.5 flex items-center gap-1">
              <Star className="w-3 h-3" fill="currentColor" />
              <span className="hidden sm:inline">{t('signature')}</span>
            </Badge>
          )}
          {item.isPopular && (
            <Badge className="bg-secondary text-secondary-foreground text-xs px-2 py-0.5 flex items-center gap-1">
              <Heart className="w-3 h-3" fill="currentColor" />
              <span className="hidden sm:inline">{t('popular')}</span>
            </Badge>
          )}
        </div>

        {/* Price tag */}
        <div className="absolute bottom-2 right-2">
          <span className="bg-background/90 backdrop-blur-sm text-primary font-display text-lg px-3 py-1 rounded-full border border-primary/30">
            {item.price === 0 && item.priceText ? getText(item.priceText) : `${t('currency')}${item.price}`}
          </span>
        </div>
      </div>

      {/* Content */}
      <div className="p-3 sm:p-4">
        <div className="flex items-start justify-between gap-2 mb-2">
          <h3 className="font-display text-base sm:text-lg text-foreground line-clamp-1 flex-1">
            {getText(item.name)}
          </h3>
          <div className="flex items-center gap-1 shrink-0">
            {item.isSpicy && <Flame className="w-4 h-4 text-secondary" />}
            {item.isVegan && <Leaf className="w-4 h-4 text-green-500" />}
            {item.isHealthy && <Heart className="w-4 h-4 text-primary" />}
          </div>
        </div>

        <p className="text-muted-foreground text-xs sm:text-sm font-body line-clamp-2 mb-3">
          {getText(item.description)}
        </p>

        {/* Allergens */}
        {item.allergens && item.allergens.length > 0 && (
          <div className="flex flex-wrap gap-1 mt-2">
            {item.allergens.slice(0, 3).map((allergen) => (
              <AllergenBadge key={allergen} allergen={allergen} />
            ))}
            {item.allergens.length > 3 && (
              <span className="text-xs text-muted-foreground">+{item.allergens.length - 3}</span>
            )}
          </div>
        )}
      </div>
    </div>
  );
};

const ItemDetailModal = ({ item, isOpen, onClose }) => {
  const { getText, t, language } = useLanguage();
  const [showVideo, setShowVideo] = useState(false);
  const [videoLoaded, setVideoLoaded] = useState(false);

  // Reset states when modal closes or item changes
  React.useEffect(() => {
    if (!isOpen) {
      setShowVideo(false);
      setVideoLoaded(false);
    }
  }, [isOpen, item?.id]);

  if (!item) return null;

  return (
    <Dialog open={isOpen} onOpenChange={onClose}>
      <DialogContent className="bg-card border-border/50 max-w-md mx-auto p-0 overflow-hidden rounded-2xl max-h-[90vh] overflow-y-auto">
        {/* Video or Image */}
        <div className="relative aspect-video bg-card overflow-hidden">
          {item.hasVideo && item.video && showVideo ? (
            <>
              {/* Thumbnail shown until video loads */}
              {!videoLoaded && (
                <img
                  src={item.image}
                  alt={getText(item.name)}
                  className={`absolute inset-0 w-full h-full ${item.objectFit === 'contain' ? 'object-contain bg-white/5' : 'object-cover'}`}
                  onError={(e) => { if (!e.currentTarget.src.endsWith('/images/menu/placeholder.webp')) e.currentTarget.src = '/images/menu/placeholder.webp'; }}
                />
              )}
              <video
                src={item.video}
                className={`w-full h-full object-cover video-clean ${videoLoaded ? 'opacity-100' : 'opacity-0'}`}
                autoPlay
                loop
                muted
                playsInline
                controls
                onLoadedData={() => setVideoLoaded(true)}
                onCanPlay={() => setVideoLoaded(true)}
                onError={(e) => console.error('Video error:', e.target.error)}
              />
            </>
          ) : (
            <img
              src={item.image}
              alt={getText(item.name)}
              className={`w-full h-full ${item.objectFit === 'contain' ? 'object-contain bg-white/5' : 'object-cover'}`}
              onError={(e) => { if (!e.currentTarget.src.endsWith('/images/menu/placeholder.webp')) e.currentTarget.src = '/images/menu/placeholder.webp'; }}
            />
          )}
          <div className="absolute inset-0 bg-gradient-to-t from-card via-transparent to-transparent pointer-events-none" />
          
          {/* Badges */}
          <div className="absolute top-4 left-4 flex flex-wrap gap-2 pointer-events-none">
            {item.isSignature && (
              <Badge className="bg-primary text-primary-foreground flex items-center gap-1">
                <Star className="w-3 h-3" fill="currentColor" />
                {t('signature')}
              </Badge>
            )}
            {item.isPopular && (
              <Badge className="bg-secondary text-secondary-foreground flex items-center gap-1">
                <Heart className="w-3 h-3" fill="currentColor" />
                {t('popular')}
              </Badge>
            )}
          </div>
        </div>

        {/* Content */}
        <div className="p-6">
          <DialogHeader className="mb-4">
            <div className="flex items-start justify-between gap-4">
              <DialogTitle className="font-display text-2xl text-foreground text-left">
                {getText(item.name)}
              </DialogTitle>
              <span className="text-primary font-display text-2xl shrink-0">
                {item.price === 0 && item.priceText ? getText(item.priceText) : `${t('currency')}${item.price}`}
              </span>
            </div>
          </DialogHeader>

          <p className="text-muted-foreground font-body leading-relaxed mb-4">
            {getText(item.description)}
          </p>

          {/* Video Button - Only show if item has video and not currently playing */}
          {item.hasVideo && item.video && !showVideo && (
            <button
              onClick={() => setShowVideo(true)}
              className="w-full mb-4 py-3 px-4 bg-primary/10 hover:bg-primary/20 border border-primary/30 rounded-xl flex items-center justify-center gap-3 transition-all duration-300 group"
            >
              <div className="w-10 h-10 rounded-full bg-primary/20 group-hover:bg-primary/30 flex items-center justify-center transition-colors">
                <Play className="w-5 h-5 text-primary ml-0.5" fill="currentColor" />
              </div>
              <span className="text-primary font-medium">
                {language === 'tr' ? 'Tanıtım videomuzu izler misiniz?' : 'Would you like to watch our video?'}
              </span>
            </button>
          )}

          {/* Stop Video Button - Show when video is playing */}
          {item.hasVideo && item.video && showVideo && (
            <button
              onClick={() => setShowVideo(false)}
              className="w-full mb-4 py-3 px-4 bg-secondary/10 hover:bg-secondary/20 border border-secondary/30 rounded-xl flex items-center justify-center gap-3 transition-all duration-300"
            >
              <X className="w-5 h-5 text-secondary" />
              <span className="text-secondary font-medium">
                {language === 'tr' ? 'Videoyu Kapat' : 'Close Video'}
              </span>
            </button>
          )}

          {/* Item attributes */}
          <div className="flex flex-wrap gap-3 mb-6">
            {item.isSpicy && (
              <div className="flex items-center gap-2 text-secondary">
                <Flame className="w-4 h-4" />
                <span className="text-sm">{t('spicy')}</span>
              </div>
            )}
            {item.isVegan && (
              <div className="flex items-center gap-2 text-green-500">
                <Leaf className="w-4 h-4" />
                <span className="text-sm">{t('vegan')}</span>
              </div>
            )}
            {item.isHealthy && (
              <div className="flex items-center gap-2 text-primary">
                <Heart className="w-4 h-4" />
                <span className="text-sm">{t('healthy')}</span>
              </div>
            )}
          </div>

          {/* Optional Extras */}
          {item.extras && item.extras.length > 0 && (
            <div className="border-t border-border/30 pt-4 mb-4">
              <div className="flex items-center gap-2 mb-3 text-muted-foreground">
                <span className="text-sm font-medium">{language === 'tr' ? 'İsteğe Bağlı Seçenekler' : 'Optional Extras'}</span>
              </div>
              <div className="flex flex-wrap gap-2">
                {item.extras.map((extra, index) => (
                  <span
                    key={index}
                    className="px-3 py-1 bg-primary/10 text-primary rounded-full text-sm border border-primary/20"
                  >
                    {getText(extra)}
                  </span>
                ))}
              </div>
            </div>
          )}

          {/* Allergens section */}
          {item.allergens && item.allergens.length > 0 && (
            <div className="border-t border-border/30 pt-4">
              <div className="flex items-center gap-2 mb-3 text-muted-foreground">
                <AlertTriangle className="w-4 h-4" />
                <span className="text-sm font-medium">{t('allergens')}</span>
              </div>
              <div className="flex flex-wrap gap-2">
                {item.allergens.map((allergen) => (
                  <span
                    key={allergen}
                    className="inline-flex items-center gap-2 px-3 py-1.5 rounded-full bg-muted text-foreground text-sm"
                  >
                    <span>{allergenInfo[allergen]?.icon}</span>
                    <span>{allergenInfo[allergen]?.[language]}</span>
                  </span>
                ))}
              </div>
            </div>
          )}
        </div>
      </DialogContent>
    </Dialog>
  );
};

const CategoryDetail = () => {
  const { categoryId } = useParams();
  const navigate = useNavigate();
  const { getText, t } = useLanguage();
  const { categories, menuItems, loading } = useMenuData();
  const [selectedItem, setSelectedItem] = useState(null);

  const category = useMemo(() => {
    return categories.find((c) => c.id === categoryId);
  }, [categoryId, categories]);

  const items = useMemo(() => {
    return menuItems[categoryId] || [];
  }, [categoryId, menuItems]);

  if (!category) {
    return (
      <div className="min-h-screen bg-background flex items-center justify-center">
        <p className="text-muted-foreground">{t('loading')}</p>
      </div>
    );
  }

  return (
    <div className="min-h-screen bg-background flex flex-col">
      <Header showBackButton={true} title={getText(category.name)} />

      {/* Category Hero */}
      <div className="relative h-32 sm:h-40 overflow-hidden">
        <img
          src={category.image}
          alt={getText(category.name)}
          className="w-full h-full object-cover"
          loading="eager"
          decoding="async"
          onError={(e) => { if (!e.currentTarget.src.endsWith('/images/menu/placeholder.webp')) e.currentTarget.src = '/images/menu/placeholder.webp'; }}
        />
        <div className="absolute inset-0 bg-gradient-to-t from-background via-background/60 to-transparent" />
        <div className="absolute bottom-4 left-4 right-4">
          <h2 className="font-display text-2xl sm:text-3xl text-foreground mb-1">
            {getText(category.name)}
          </h2>
          <p className="text-muted-foreground text-sm font-body">
            {items.length} {t('products').toLowerCase()}
          </p>
        </div>
      </div>

      {/* Items Grid */}
      <main className="flex-1 px-4 py-6 pb-24">
        {items.length > 0 ? (
          <div className="grid grid-cols-1 sm:grid-cols-2 gap-4 max-w-2xl mx-auto">
            {items.map((item, index) => (
              <MenuItemCard
                key={item.id}
                item={item}
                onClick={setSelectedItem}
                index={index}
              />
            ))}
          </div>
        ) : (
          <div className="flex items-center justify-center h-64">
            <p className="text-muted-foreground text-center">{t('noItems')}</p>
          </div>
        )}
      </main>

      <Footer />

      {/* Item Detail Modal */}
      <ItemDetailModal
        item={selectedItem}
        isOpen={!!selectedItem}
        onClose={() => setSelectedItem(null)}
      />
    </div>
  );
};

export default CategoryDetail;
